import React, { useEffect, useState } from "react";
import {
  AnalysisInputParams,
  FilterUserAverageScoreRange,
  StockTriggerType
} from "predictagram-lib";
import { FieldArray } from "formik";
import { DropDown } from "components/common/form/DropDown";
import { PredictionTypesDropdown } from "components/common/PredictionsDropDown";
import { FieldWithError } from "components/common";
import { AnalysisFilters } from "components/common/analysis/analysis.filters";

import { Helper } from "_utils";
import { SignalSetupComp } from "components/admin/common/SignalSetupComp";

import { StrategyProfitModel, StrategyProfitModel as model } from "models/strategy-profit.model";
import { DatePickerTzEst } from "components/common/DatePickerTz";
import { MultiSymbolsDropDown } from "components/common/analysis/MultiSymbolsDropDown";
import { SymbolIdsDropdown } from "components/common/SymbolIdsDropdown";
import { TradeSetupFilterOptions } from "../autotrade/TradeSetupForm";
import { StrategyFilterOptions } from "../analysis/strategy-profit/Filter";


interface IProps {
  errors: any,
  touched: any,
  setFieldValue: any,
  values: any,
  initialValues: any,
  onOpenTriggerDirection?: (direction: "up" | "dn") => void,
  onOpenSignalDirection?: (direction: "up" | "dn") => void,
}

export const Criteria: React.FunctionComponent<IProps> = ({
  errors,
  touched,
  setFieldValue,
  values,
  initialValues,
  onOpenTriggerDirection,
  onOpenSignalDirection
}) => {

  const [trigger, setTrigger] = useState<StockTriggerType>(StockTriggerType.PREDICTION);
  const [showSecondOpenSignal, setShowSecondOpenSignal] = useState<boolean>(false);
  const [showSecondCloseSignal, setShowSecondCloseSignal] = useState<boolean>(false);
  const [enabledExtraSymbolIds, setEnabledExtraSymbolIds] = useState<boolean>(initialValues.extraSymbolIds?.length > 0);

  useEffect(() => {
    const initialTrigger = (() => {
      return initialValues.openTriggerType || StockTriggerType.PREDICTION;
    })();

    setTrigger(initialTrigger);
    if (initialValues?.openSignalSetup?.signals && initialValues?.openSignalSetup.signals.length > 1) {
      setShowSecondOpenSignal(true);
    }

    if (initialValues?.closeSignalSetup?.signals && initialValues?.closeSignalSetup.signals.length > 1) {
      setShowSecondCloseSignal(true);
    }

    setEnabledExtraSymbolIds(initialValues.extraSymbolIds?.length > 0);

  }, [initialValues])


  return (

    <div className="d-flex justify-content-start gap-3 analysis">
      <div className="d-flex flex-column gap-2">
        <SymbolIdsDropdown name="stockSymbolId" errors={errors} touched={touched} />

        <DropDown defaultItem={{ key: '', value: 'None' } as any} onChange={(v: any) => { setFieldValue('prevCurDayPriceGap[direction]', Helper.toIntOrDefault(v.target.value)) }} enumMap={model.gapDirection} errors={errors} touched={touched} label="Gap Dir." name={'prevCurDayPriceGap[direction]'} />
        <DropDown onChange={(v: any) => { setFieldValue('prevCurDayPriceGap[diffMinRate]', parseFloat(v.target.value)) }} enumMap={model.signalPrevClosePriceThreshold} errors={errors} touched={touched} label="Gap Diff %" name={'prevCurDayPriceGap[diffMinRate]'} />

        <MultiSymbolsDropDown errors={errors} touched={touched} name={'extraSymbolIds'} enableByDefault={enabledExtraSymbolIds} />
        {/*<SymbolIdsDropdown name="extraSymbolIds" errors={errors} touched={touched}  multiple={true}/>*/}

        <DropDown onChange={(v: any) => { setTrigger(parseInt(v.target.value)); setFieldValue('openTriggerType', v.target.value) }} enumMap={model.triggerType} errors={errors} touched={touched} label="Open By" name={'openTriggerType'} />

        <DropDown
          errors={errors}
          touched={touched}
          multiple={true}
          enumMap={model.directionTypeList}
          label={'Open Trigger Direction'}
          name={'openTriggerDirectionTypes'}
          onChange={(v: any) => {
            const openTriggerTypes = StrategyProfitModel.toIntArrayOrDefault(Array.from(v.target.selectedOptions).map((o: any) => o.value));
            setFieldValue(`openTriggerDirectionTypes`, openTriggerTypes);
            if (onOpenTriggerDirection && openTriggerTypes.length > 0) {
              onOpenTriggerDirection(openTriggerTypes[0] === 1 ? "up" : "dn");
            }
          }} />

        <div>
          <PredictionTypesDropdown name="predictionTypes" showDefault={false} errors={errors} touched={touched} multiple={true} />
          <FieldWithError errors={errors} touched={touched} fieldName="predictionIds" label="Prediction IDs" />
          <FieldWithError errors={errors} touched={touched} fieldName="predictionUserIds" label="User IDs" />
          <DropDown enumMap={model.closeStepSizeRateList} defaultItem={{ key: '', value: 'None' } as any} errors={errors} touched={touched} label="Step Size First Min Move" name={'predictionFirstMinStepSizeMoveMax'} />

        </div>

        {trigger === StockTriggerType.SIGNAL && <>
          <FieldArray name="openSignalSetup.signals" render={() => (<>
            <SignalSetupComp initialSignalSelected={values.openSignalSetup?.signals && values.openSignalSetup?.signals.length > 0} setupName={'openSignalSetup.signals[0]'} setFieldValue={setFieldValue} errors={errors} touched={touched} onOpenSignalDirection={onOpenSignalDirection}/>
            <DropDown enumMap={model.signalPrevClosePriceThreshold} errors={errors} touched={touched} label="PrevClosePrice % Adjust" name={'openSignalSetup.prevClosePriceThreshold'} onChange={(v: any) => { setFieldValue('openSignalSetup.prevClosePriceThreshold', v.target.value) }} /><br />
            <button type="button" onClick={() => { 
              if (showSecondOpenSignal) {
                setFieldValue('openSignalSetup[signals][1]', undefined);
              }
              setShowSecondOpenSignal(!showSecondOpenSignal); 
            }}>{showSecondOpenSignal ? 'Hide' : 'Add'} Second Signal</button>
            {showSecondOpenSignal && <>
              <DropDown enumMap={model.signalIntresectionInterval} errors={errors} touched={touched} label="Intersec. Interval" name={'openSignalSetup.intersectionSecs'} onChange={(v: any) => { setFieldValue('openSignalSetup.intersectionSecs', Helper.toIntOrDefault(v.target.value)) }} /><br />
              <SignalSetupComp initialSignalSelected={values.openSignalSetup?.signals && values.openSignalSetup?.signals.length > 1} setFieldValue={setFieldValue} setupName={'openSignalSetup.signals[1]'} errors={errors} touched={touched} onOpenSignalDirection={onOpenSignalDirection}/>
            </>}
          </>
          )} />
        </>
        }
      </div>

      <div className="d-flex flex-column gap-3">
        <DropDown enumMap={model.groupDirType} errors={errors} touched={touched} label="DirSum Type" name={'groupDirectionType'} />
        <DropDown enumMap={model.windowTimeSecs} errors={errors} touched={touched} label="Window Min" name={'windowTimeSecs'} />
        <DropDown enumMap={model.windowMinPreds} errors={errors} touched={touched} label="Min # Involved" name={'windowMinElements'} />
        <DropDown enumMap={model.aheadTimeSecs} errors={errors} touched={touched} label="Ahead Min" name={'aheadTimeSecs'} />
        <DropDown enumMap={AnalysisFilters.UsersScore}
          errors={errors}
          touched={touched}
          label="Last30d Score" name="last30dScore" />
      </div>
      <div className="d-flex flex-column gap-3">
        <DropDown enumMap={model.takeProfitStopLossRate.takeProfitRate} errors={errors} touched={touched} label="Take Profit %" name={'takeProfitRate'} />
        <DropDown enumMap={model.takeProfitStopLossRate.lossRate} errors={errors} touched={touched} label="Stop Loss %" name={'stopLossRate'} />
      </div>

      <div className="d-flex flex-column">
        <div><b>Close By</b></div>
        <div className="my-2">
          <DropDown enumMap={model.closeStepSizeRateList} defaultItem={{ key: '', value: 'None' } as any} errors={errors} touched={touched} label="Step Size" name={'closeStepSizeRate'} />
        </div>
        <SignalSetupComp initialSignalSelected={values.closeSignalSetup?.signals && values.closeSignalSetup?.signals.length > 0} setFieldValue={setFieldValue} setupName={'closeSignalSetup[signals][0]'} errors={errors} touched={touched} />
        <DropDown enumMap={model.signalPrevClosePriceThreshold} errors={errors} touched={touched} label="PrevClosePrice % Adjust" name={'closeSignalSetup.prevClosePriceThreshold'} onChange={(v: any) => { setFieldValue('closeSignalSetup.prevClosePriceThreshold', parseFloat(v.target.value)) }} /><br />
        <button type="button" onClick={() => { 
            if (showSecondCloseSignal) {
              setFieldValue('closeSignalSetup[signals][1]', undefined);
            }
            setShowSecondCloseSignal(!showSecondCloseSignal); 
          }}>{showSecondCloseSignal ? 'Hide' : 'Add'} Second Signal</button>
        {showSecondCloseSignal && <>
          <DropDown enumMap={model.signalIntresectionInterval} errors={errors} touched={touched} label="Intersec. Interval" name={'closeSignalSetup.intersectionSecs'} onChange={(v: any) => { setFieldValue('closeSignalSetup.intersectionSecs', Helper.toIntOrDefault(v.target.value)) }} /><br />
          <SignalSetupComp initialSignalSelected={values.closeSignalSetup?.signals && values.closeSignalSetup?.signals.length > 1} setFieldValue={setFieldValue} setupName={'closeSignalSetup[signals][1]'} errors={errors} touched={touched} />
        </>}
      </div>

      <div className="d-flex flex-column">
        <span className="fw-bold">Start Date EST (inclusive)</span>
        <DatePickerTzEst
          dateFormat="MM/dd/yyyy"
          onChange={(date: Date) => {
            setFieldValue("startTime", date.getTime() / 1000);
          }}
          selected={new Date(values.startTime * 1000)}
        />
        <br />
        <span className="fw-bold">End Date EST (inclusive)</span>
        <DatePickerTzEst
          dateFormat="MM/dd/yyyy"
          onChange={(date: Date) => {
            setFieldValue("endTime", date.getTime() / 1000);
          }}
          selected={new Date(values.endTime * 1000)}
        />
      </div>

      <div className="d-flex flex-column">
        <DropDown enumMap={model.startHour} errors={errors} touched={touched} label="Start Hour EST (inclusive)" name={'startHourEst'} />
        <DropDown enumMap={model.endHour} errors={errors} touched={touched} label="End Hour EST (inclusive)" name={'endHourEst'} />
      </div>
    </div>
  )
}


export const processSearchOptions = async (searchOptions: TradeSetupFilterOptions | StrategyFilterOptions) => {
  if (searchOptions.predictionIds?.length) {
    searchOptions.predictionIds = (searchOptions.predictionIds as any as string).split(',').map(v => +v);
  } else {
    searchOptions.predictionIds = [];
  }
  if (searchOptions.predictionUserIds?.length) {
    searchOptions.predictionUserIds = (searchOptions.predictionUserIds as any as string).split(',').map(v => +v);
  } else {
    searchOptions.predictionUserIds = undefined;
  }
  const arrayToInts = (field: string) => {
    const opts = searchOptions as any;
    if (opts[field] && opts[field]?.length) {
      opts[field] = opts[field].map((v: any) => +v);
    } else {
      opts[field] = []
    }
  }

  if (searchOptions.openSignalSetup && searchOptions.openSignalSetup.signals) {
    searchOptions.openSignalSetup.signals = searchOptions.openSignalSetup.signals.filter(s => s !== undefined );
  }

  if (searchOptions.closeSignalSetup && searchOptions.closeSignalSetup.signals) {
    searchOptions.closeSignalSetup.signals = searchOptions.closeSignalSetup.signals.filter(s => s !== undefined );
  }

  for (let setup of [searchOptions.openSignalSetup, searchOptions.closeSignalSetup]) {
    (setup?.signals || []).forEach((s, index) => {
      if (!s.type) {
        (setup as any).signals = setup?.signals.filter((v, index2) => index2 !== index);
      }
    });
    if (!setup?.signals?.length) {
      (setup as any).signals = [];
      setup = undefined;
    }
  }

  arrayToInts('predictionTypes');

  const score = +(searchOptions.last30dScore);
  if (score > 0) {
    searchOptions.userAverageScore = {
      days: 30,
      minScoreRaw: score,
    } as any;
  } else if (score < 0) {
    searchOptions.userAverageScore = {
      days: 30,
      maxScoreRaw: score * -1,
    } as any;
  } else {
    delete searchOptions.userAverageScore;
  }

  for (const k in searchOptions) {
    const v = (searchOptions as any)[k];
    if (v && !Array.isArray(v) && !isNaN(Number(v)) && Number(v) !== 0 && v !== true && v !== false) {
      (searchOptions as any)[k] = +v;
    }
  }

}

export const getAnalysisInputParams = (options: StrategyFilterOptions | TradeSetupFilterOptions): Partial<AnalysisInputParams> => {

  const extraSymbolIds = options.extraSymbolIds ? options.extraSymbolIds.map((s) => parseInt(s.toString())) : undefined;

  const inputParams: Partial<AnalysisInputParams> = {
    aheadTimeSecs: options.aheadTimeSecs,
    prevCurDayPriceGap: options.prevCurDayPriceGap,
    closeStepSizeRate: options.closeStepSizeRate,
    endHourEst: options.endHourEst,
    closeSignalSetup: options.closeSignalSetup,
    endTime: options.endTime,
    groupDirectionType: options.groupDirectionType,
    openSignalSetup: options.openSignalSetup,
    openTriggerType: options.openTriggerType,
    predictionIds: options.predictionIds?.length ? options.predictionIds.join(',') : undefined as any,
    predictionUserIds: options.predictionUserIds?.length ? options.predictionUserIds.join(',') : undefined as any,
    predictionTypes: options.predictionTypes,
    startHourEst: options.startHourEst,
    startTime: options.startTime,
    stockSymbolId: options.stockSymbolId,
    stopLossRate: options.stopLossRate,
    takeProfitRate: options.takeProfitRate,
    userAverageScore: options.userAverageScore,
    windowMinElements: options.windowMinElements,
    windowTimeSecs: options.windowTimeSecs,
    openTriggerDirectionTypes: options.openTriggerDirectionTypes,
    predictionFirstMinStepSizeMoveMax: options.predictionFirstMinStepSizeMoveMax,
    extraSymbolIds: extraSymbolIds,
  };

  return inputParams;
}

export const getLast30dScore = (userAverageScore: FilterUserAverageScoreRange | undefined) => {
  if (!userAverageScore) {
    return undefined;
  }

  if (userAverageScore.minScoreRaw) {
    return userAverageScore.minScoreRaw
  }

  if (userAverageScore.maxScoreRaw) {
    return userAverageScore.maxScoreRaw * -1
  }

}